import { ContentCalendarDay, ContentNetwork, } from '@wit-support/admin-contracts';
import { CONTENT_CHART_PRESETS, } from '@wit-support/ui-domain';
import { periodPatch, resolvePeriod } from '../period.js';
/**
 * Чистая логика «Обзора»: период из адреса, параметры запросов, данные
 * графиков и топ клипов. Без React — проверяется юнит-тестами.
 */
export const DEFAULT_PRESET = '28d';
const isDay = (s) => s !== undefined && ContentCalendarDay.safeParse(s).success;
/**
 * Период графиков из адреса: `from` и `to` (`YYYY-MM-DD`, from ≤ to) — свой,
 * иначе `period` ∈ 7d|28d|90d, иначе 28 дней.
 */
export function periodFromSearch(s) {
    if (isDay(s.from) && isDay(s.to) && s.from <= s.to) {
        return { kind: 'custom', from: s.from, to: s.to };
    }
    const preset = CONTENT_CHART_PRESETS.find((p) => p === s.period) ?? DEFAULT_PRESET;
    return { kind: 'preset', preset };
}
/**
 * Правка адреса для нового периода — та же схема, что у «Публикаций» и
 * «Журнала решений» (`period.ts`): пресет по умолчанию — без параметра.
 */
export function periodToSearch(p) {
    return periodPatch(p.kind === 'custom' ? { choice: 'custom', from: p.from, to: p.to } : { choice: p.preset });
}
/**
 * Свои дни → ISO-границы суток по зоне расписания движка (в ней же движок режет
 * точки рядов): с начала первого дня до начала дня после последнего.
 */
function customIso(p) {
    const r = resolvePeriod({ choice: 'custom', from: p.from, to: p.to });
    // Оба дня проверены `periodFromSearch`; запасной вариант — на случай DST-дыры.
    return { from: r.from ?? `${p.from}T00:00:00.000Z`, to: r.to ?? `${p.to}T23:59:59.999Z` };
}
/**
 * Период запроса рядов. У рядов нет пресета «7 дней» — берётся 28 дней от
 * часов СЕРВЕРА, а неделя отрезается по последнему дню ответа
 * ({@link trimSeriesToPreset}): часы браузера в расчёт периода не идут.
 */
export function seriesRange(p) {
    if (p.kind === 'custom')
        return customIso(p);
    return { period: p.preset === '90d' ? '90d' : '28d' };
}
export function publicationsRange(p) {
    if (p.kind === 'custom')
        return customIso(p);
    return { period: p.preset };
}
const DAY_MS = 24 * 60 * 60 * 1000;
function shiftDay(day, days) {
    return new Date(Date.parse(`${day}T00:00:00.000Z`) + days * DAY_MS).toISOString().slice(0, 10);
}
/**
 * Пресет «7 дней» поверх ответа за 28: дни — последние 7 по последней дате
 * ряда; недели — те, что пересекают эти 7 дней. Прочие периоды — как есть.
 */
export function trimSeriesToPreset(points, p, bucket) {
    if (p.kind !== 'preset' || p.preset !== '7d' || points.length === 0)
        return [...points];
    const last = points.reduce((m, x) => (x.date > m ? x.date : m), points[0].date);
    const lastDay = bucket === 'week' ? shiftDay(last, 6) : last;
    const firstDay = shiftDay(lastDay, -6);
    return points.filter((x) => bucket === 'day' ? x.date >= firstDay : shiftDay(x.date, 6) >= firstDay);
}
/** Сети ряда в постоянном порядке (цвет и легенда не прыгают от фильтра). */
export function seriesNetworks(points) {
    const present = new Set(points.map((x) => x.network));
    return ContentNetwork.options.filter((n) => present.has(n));
}
/** Строки графика «просмотры по дням»: `{ date, instagram: n, tiktok: n, … }`. */
export function viewsByDayRows(points) {
    const byDate = new Map();
    for (const x of points) {
        const row = byDate.get(x.date) ?? { date: x.date };
        row[x.network] = (Number(row[x.network] ?? 0) || 0) + x.viewsTotal;
        byDate.set(x.date, row);
    }
    return [...byDate.values()].sort((a, b) => String(a.date).localeCompare(String(b.date)));
}
/** Строки графика «по неделям»: сумма по сетям. */
export function weeklyRows(points) {
    const byDate = new Map();
    for (const x of points) {
        const row = byDate.get(x.date) ?? { date: x.date, publications: 0, hits: 0, flops: 0 };
        row.publications += x.publications;
        row.hits += x.hits;
        row.flops += x.flops;
        byDate.set(x.date, row);
    }
    return [...byDate.values()].sort((a, b) => a.date.localeCompare(b.date));
}
/**
 * Топ клипов периода: ролики группируются по первому клипу, клипы — по сумме
 * просмотров «первым». Ролики без первого клипа (старая склейка без решения)
 * не участвуют.
 */
export function topClipsOf(items, limit = 8) {
    const by = new Map();
    for (const p of items) {
        if (p.hookClipId === null)
            continue;
        const views = p.views ?? 0;
        const cur = by.get(p.hookClipId) ?? {
            clipId: p.hookClipId,
            name: p.hookClipName,
            views: 0,
            publications: 0,
            bestMultiple: null,
            networks: [],
            topPublicationId: p.id,
            previewUrl: null,
            posterUrl: null,
            topViews: -1,
        };
        cur.previewUrl ??= p.hookPreviewUrl;
        cur.posterUrl ??= p.hookPosterUrl;
        cur.views += views;
        cur.publications += 1;
        cur.name ??= p.hookClipName;
        if (!p.maturing && p.multiple !== null) {
            cur.bestMultiple =
                cur.bestMultiple === null ? p.multiple : Math.max(cur.bestMultiple, p.multiple);
        }
        if (!cur.networks.includes(p.network))
            cur.networks.push(p.network);
        if (views > cur.topViews) {
            cur.topViews = views;
            cur.topPublicationId = p.id;
        }
        by.set(p.hookClipId, cur);
    }
    return [...by.values()]
        .sort((a, b) => b.views - a.views || b.publications - a.publications)
        .slice(0, limit)
        .map(({ topViews: _drop, ...rest }) => ({
        ...rest,
        networks: ContentNetwork.options.filter((n) => rest.networks.includes(n)),
    }));
}
//# sourceMappingURL=overview-data.js.map